"use client";

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { IconCheck, IconLink, IconShare } from "@/components/ui/icons";
import { track } from "@/features/analytics/runtime";

// Paylaşım düğmelerinin istemci parçaları (share-bar.tsx): bağlantıyı kopyalama ve dar ekrandaki tek "Paylaş" düğmesi.
// Ağ bağlantıları sunucuda çizilir (düz <a>); burada yalnızca pano, yerel paylaşım sayfası ve açılır kutu davranışı var.

const COPIED_MS = 2500;

/** Pano: Clipboard API (HTTPS), yoksa gizli metin alanı + execCommand (eski tarayıcılar) */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // izin yok: yedek yönteme geç
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

type CopyLabels = { copy: string; copied: string; copiedLong: string; failed: string };

/**
 * "Bağlantıyı kopyala". icon: yuvarlak simge düğmesi (geniş ekranda bölüm menüsünün sağında; kopyalanınca altında kısa
 * "Kopyalandı" etiketi); item: açılır kutudaki satır. Sonuç ekran okuyucuya role="status" (aria-live polite) ile okunur.
 */
export function CopyLinkButton({ url, labels, variant }: { url: string; labels: CopyLabels; variant: "icon" | "item" }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number | null>(null);
  useEffect(() => () => void (timer.current !== null && window.clearTimeout(timer.current)), []);

  const onClick = async () => {
    const ok = await copyText(url);
    setState(ok ? "copied" : "failed");
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), COPIED_MS);
  };
  const copied = state === "copied";
  const announce = state === "copied" ? labels.copiedLong : state === "failed" ? labels.failed : "";
  const icon = copied ? <IconCheck size={20} className="text-success" /> : <IconLink size={20} />;

  return (
    <span className={`relative inline-flex ${variant === "item" ? "w-full" : ""}`}>
      {variant === "icon" ? (
        <button type="button" onClick={onClick} data-share-method="copy" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink-muted hover:bg-primary-soft hover:text-ink">
          {icon}
          <span className="sr-only">{labels.copy}</span>
        </button>
      ) : (
        <button type="button" onClick={onClick} data-share-method="copy" className="flex h-11 w-full items-center gap-3 rounded-md px-3 text-left font-semibold text-ink hover:bg-primary-soft">
          <span aria-hidden="true" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-alt text-ink">
            {icon}
          </span>
          {/* Görünür yazı değişse de düğmenin adı aynı kalır (sonuç durum bölgesinden okunur) */}
          <span aria-hidden="true">{copied ? labels.copied : labels.copy}</span>
          <span className="sr-only">{labels.copy}</span>
        </button>
      )}
      {variant === "icon" && state !== "idle" && (
        <span aria-hidden="true" className="pointer-events-none absolute top-full right-0 z-20 mt-1.5 rounded-md bg-ink px-2 py-1 text-xs font-semibold whitespace-nowrap text-surface shadow-raised">
          {copied ? labels.copied : labels.failed}
        </span>
      )}
      <span role="status" className="sr-only">
        {announce}
      </span>
    </span>
  );
}

const WIDTH = 256;
const GAP = 6;

/**
 * Dar ekranda tek "Paylaş" düğmesi: tarayıcı destekliyorsa (çoğu telefon) yerel paylaşım sayfası (navigator.share),
 * değilse aynı seçenekleri listeleyen kutu. Kutu HTML popover (üst katman): bölüm menüsünün yatay kaydırmasında
 * kırpılmaz; Esc ve dışarı dokunma tarayıcı tarafından kapatır, kapanınca odak düğmeye döner. Düğme popovertarget ile
 * bağlı: JavaScript yüklenmeden de kutu açılır. Kutudaki bir bağlantı seçilince kutu kapanır.
 */
export function ShareMenu({
  label,
  title,
  shareData,
  className = "",
  children,
}: {
  label: string;
  title: string;
  shareData: { title: string; text: string; url: string };
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);

  const isOpen = () => popover.current?.matches(":popover-open") ?? false;

  // Düğmenin altına, sağ kenarı düğmeyle hizalı (yer yoksa üstüne); ekran kenarından taşmaz
  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect();
    const p = popover.current;
    if (!b || !p) return;
    const width = Math.min(WIDTH, window.innerWidth - 16);
    const left = Math.min(Math.max(8, b.right - width), window.innerWidth - width - 8);
    const below = b.bottom + GAP;
    const top = below + p.offsetHeight > window.innerHeight - 8 ? Math.max(8, b.top - GAP - p.offsetHeight) : below;
    p.style.left = `${left}px`;
    p.style.top = `${top}px`;
  }, []);

  useEffect(() => {
    const p = popover.current;
    if (!p) return;
    // beforetoggle: yer açılmadan önce ayarlanır (sol üst köşede bir kare görünmesin); toggle: yüksekliğe göre düzeltilir
    const onToggle = (e: Event) => {
      const open = (e as ToggleEvent).newState === "open";
      if (e.type === "toggle") button.current?.setAttribute("aria-expanded", String(open));
      if (open) place();
    };
    const onMove = () => {
      if (isOpen()) place();
    };
    p.addEventListener("beforetoggle", onToggle);
    p.addEventListener("toggle", onToggle);
    window.addEventListener("scroll", onMove, { passive: true });
    window.addEventListener("resize", onMove);
    return () => {
      p.removeEventListener("beforetoggle", onToggle);
      p.removeEventListener("toggle", onToggle);
      window.removeEventListener("scroll", onMove);
      window.removeEventListener("resize", onMove);
    };
  }, [place]);

  return (
    <div className={className}>
      <button
        ref={button}
        type="button"
        aria-expanded="false"
        aria-controls={id}
        popoverTarget={id}
        onClick={(e) => {
          // Yerel paylaşım sayfası varsa kutu açılmaz (popovertarget'ın varsayılan davranışı engellenir)
          if (typeof navigator.share !== "function" || (navigator.canShare && !navigator.canShare(shareData))) return;
          e.preventDefault();
          const item = e.currentTarget.closest("[data-share]")?.getAttribute("data-share-item");
          navigator
            .share(shareData)
            // Analitik (yalnızca onay varsa): paylaşım sayfası tamamlandı
            .then(() => item && track("share", { method: "native", content_type: "tour", item_id: item }))
            .catch(() => {
              // kullanıcı vazgeçti ya da paylaşım reddedildi: bir şey yapılmaz
            });
        }}
        className="inline-flex h-11 items-center gap-2 rounded-full px-3.5 text-sm font-semibold text-ink ring-1 ring-line-strong hover:bg-primary-soft"
      >
        <IconShare size={18} />
        {label}
      </button>
      <div
        ref={popover}
        id={id}
        popover="auto"
        role="dialog"
        aria-label={title}
        onClick={(e) => {
          if ((e.target as Element).closest("a")) popover.current?.hidePopover();
        }}
        className="fixed m-0 max-w-[calc(100vw-1rem)] rounded-lg border-0 bg-surface p-2 text-ink shadow-raised ring-1 ring-line [inset:auto]"
        style={{ width: WIDTH }}
      >
        <p aria-hidden="true" className="px-3 pt-1.5 pb-2 text-xs font-bold tracking-[0.08em] text-ink-muted uppercase">
          {title}
        </p>
        {children}
      </div>
    </div>
  );
}
